import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { noop } from 'ui-components/default-props/noop';
import { emptyArray } from 'ui-components/default-props/empty';

import Global from '../../../../../store/global-selectors';
import { useManageSubscriptionDrawerContext } from '../../context/manage-subscription-drawer-context';
import { BillingPlanIds, isBillingPlanIdUsageBilledHighVolume } from '../../../../../enums/billing-plan-ids';
import { useIsNonWalletAccessVisible } from '../../../../../hooks/non-wallet-access-hooks';
import { simplifyPlans } from './helper';
import { SimplifiedPlan } from './simplified-plan';
import style from './style.module.less';

export const SimplifiedPlanGrid = ({ onSelectPlan = noop }) => {
  const { selectedPlanId, setByoaEnabled, recommendedPlanIds } = useManageSubscriptionDrawerContext();
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const shouldShowByoaSection = useIsNonWalletAccessVisible();
  const { allPlans = [], currentBillingPlan = {}, planToNonWalletAccessPlanMap = {}, currentSubscription } = subscriptionData ?? {};
  const currentBillingPlanId = currentBillingPlan?.billingPlanId;
  // currentBillingPlanId can be a BYOA id
  const [nonByoaCurrentPlanId] =
    Object.entries(planToNonWalletAccessPlanMap).find(([, bp]) => bp.billingPlanId === currentBillingPlanId) ?? emptyArray;
  // selectedPlanId can be a BYOA id
  const [nonByoaSelectedPlanId] =
    Object.entries(planToNonWalletAccessPlanMap).find(([, bp]) => bp.billingPlanId === selectedPlanId) ?? emptyArray;

  const normalizedCurrentPlanId = nonByoaCurrentPlanId ?? currentBillingPlanId;
  const normalizedSelectedPlanId = nonByoaSelectedPlanId ?? selectedPlanId;

  const { pendingPlanCode, currentPeriodEndDateTimeUTC } = currentSubscription ?? {};

  // Preserve legacy behavior: non-trial users see current plan as highlighted
  const effectiveRecommendedPlanIds = useMemo(() => {
    const isNonTrialUser = normalizedCurrentPlanId && parseInt(currentBillingPlanId, 10) !== BillingPlanIds.Trial;
    if (isNonTrialUser) {
      return [normalizedCurrentPlanId];
    }
    return recommendedPlanIds;
  }, [normalizedCurrentPlanId, currentBillingPlanId, recommendedPlanIds]);

  const onSelectPlanClick = useCallback(
    (currentlySelectedPlanId) => {
      onSelectPlan(currentlySelectedPlanId);
      const isHighVolume = isBillingPlanIdUsageBilledHighVolume(currentlySelectedPlanId);

      if (shouldShowByoaSection && isHighVolume) {
        setByoaEnabled(true);
      }
    },
    [onSelectPlan, setByoaEnabled, shouldShowByoaSection],
  );

  const highlightedBillingPlanIds = useMemo(
    () => allPlans.filter((plan) => effectiveRecommendedPlanIds.includes(plan.billingPlanId)).map((plan) => plan.billingPlanId),
    [allPlans, effectiveRecommendedPlanIds],
  );

  const simplifiedPlans = simplifyPlans(allPlans);

  return (
    <div className={style['plan-grid-container']}>
      {simplifiedPlans.map((plans) => {
        return (
          <SimplifiedPlan
            key={plans[0].billingPlanId}
            currentBillingPlanId={normalizedCurrentPlanId}
            currentPeriodEndDateTimeUTC={currentPeriodEndDateTimeUTC}
            hasPendingPlanChange={!!pendingPlanCode}
            highlightedBillingPlanIds={highlightedBillingPlanIds}
            onSelectPlanClick={onSelectPlanClick}
            selectedPlanId={normalizedSelectedPlanId}
            subPlans={plans}
          />
        );
      })}
    </div>
  );
};

SimplifiedPlanGrid.propTypes = {
  onSelectPlan: PropTypes.func,
};
